/**
 * Header Orbe mount (#custom__md-footer-selector-block):
 *   - shorten Orbe's "Country / Local / LANG" compound label
 *   - append a theme-matching chevron
 *   - clamp width so Orbe's inner wrappers don't push the language selector away
 *   - swap from SSR placeholder to live mount in one step (no reflow flash)
 *   - open geolocation popup without re-detecting location (openModalWithoutRefresh)
 */
(function () {
  const MOUNT_ID = 'custom__md-footer-selector-block';
  const WRAP_SELECTOR = '.localization__selector--orbe';

  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  function getMount() {
    return document.getElementById(MOUNT_ID);
  }

  function getWrap() {
    const m = getMount();
    return m ? m.closest(WRAP_SELECTOR) : null;
  }

  function findTrigger(root) {
    if (!root) return null;
    const direct = root.querySelector(
      'button, [role="button"], a[href], .md-orbe-country-selector, [class*="footer-selector"] button'
    );
    if (direct) return direct;
    return root.querySelector('button, [role="button"], a');
  }

  function openOrbeGeolocationModal() {
    if (typeof orbito !== 'undefined' && typeof orbito.openModalWithoutRefresh === 'function') {
      orbito.openModalWithoutRefresh();
      return true;
    }
    return false;
  }

  function bindOpenWithoutRefresh(trigger) {
    if (!trigger || trigger.dataset.orbeNoRefreshBound === 'true') return;
    trigger.dataset.orbeNoRefreshBound = 'true';

    trigger.addEventListener(
      'click',
      (e) => {
        if (!openOrbeGeolocationModal()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
      },
      true
    );
  }

  function compoundLabelStillCompound(trigger) {
    return /\S\s*\/\s*\S/.test((trigger.innerText || '').trim());
  }

  function shortenCompoundLabel(scope) {
    if (!scope || scope.closest?.('svg')) return;
    const skipTags = new Set(['SVG', 'PATH', 'STYLE', 'SCRIPT']);

    function shortenLeaf(el) {
      if (!el || skipTags.has(el.tagName)) return false;
      if (el.tagName === 'SVG') return false;

      const kids = el.children;
      if (kids && kids.length) {
        for (let i = 0; i < kids.length; i++) {
          const c = kids[i];
          if (c.tagName === 'SVG' && c.classList.contains('orbe-market-selector__chevron')) continue;
          if (shortenLeaf(c)) return true;
        }
        return false;
      }
      const t = el.textContent;
      if (!t || !/\S\s*\/\s*\S/.test(t)) return false;
      const first = t.split(/\s*\/\s*/)[0].trim();
      if (!first) return false;
      el.textContent = first;
      return true;
    }

    shortenLeaf(scope);
  }

  function ensureThemeChevron(trigger) {
    if (!trigger || trigger.querySelector('svg.orbe-market-selector__chevron')) return;
    const tmpl = document.createElement('template');
    tmpl.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" class="icon orbe-market-selector__chevron" role="presentation" focusable="false" aria-hidden="true">' +
      '<path d="M20 8.5 12.5 16 5 8.5" stroke="currentColor" stroke-width="1.5" fill="none"/>' +
      '</svg>';
    trigger.appendChild(tmpl.content.firstElementChild);
  }

  /** Force every wrapper between trigger and mount to the trigger's own width — no slack. */
  function clampWidths(mount, trigger) {
    void trigger.offsetWidth;
    const w = Math.ceil(
      Math.max(trigger.getBoundingClientRect().width, trigger.scrollWidth, 1)
    );
    if (!w) return;

    const wrap = mount.closest(WRAP_SELECTOR);
    [mount, wrap].forEach((el) => {
      if (!el) return;
      el.style.width = w + 'px';
      el.style.maxWidth = w + 'px';
      el.style.minWidth = '0';
      el.style.flex = '0 0 auto';
    });

    let node = trigger.parentElement;
    while (node && node !== mount) {
      node.style.width = w + 'px';
      node.style.maxWidth = w + 'px';
      node.style.minWidth = '0';
      node.style.flex = '0 0 auto';
      node.style.padding = '0';
      node.style.margin = '0';
      node = node.parentElement;
    }
  }

  function setState(state) {
    const wrap = getWrap();
    if (!wrap) return;
    if (state === 'ready') {
      wrap.classList.remove('orbe-market-selector--hydrating');
      wrap.classList.add('orbe-market-selector--ready');
      wrap.removeAttribute('aria-busy');
    } else {
      wrap.classList.remove('orbe-market-selector--ready');
      wrap.classList.add('orbe-market-selector--hydrating');
      wrap.setAttribute('aria-busy', 'true');
    }
  }

  function isReady() {
    const wrap = getWrap();
    return !!(wrap && wrap.classList.contains('orbe-market-selector--ready'));
  }

  function process() {
    const mount = getMount();
    if (!mount) return false;

    const trigger = findTrigger(mount);
    if (!trigger) return false;

    shortenCompoundLabel(trigger);
    ensureThemeChevron(trigger);
    bindOpenWithoutRefresh(trigger);
    return !compoundLabelStillCompound(trigger);
  }

  function finalizeWidths() {
    const mount = getMount();
    if (!mount) return;
    const trigger = findTrigger(mount);
    if (trigger) clampWidths(mount, trigger);
  }

  function tryReveal() {
    const ok = process();
    if (!ok) return false;
    setState('ready');
    requestAnimationFrame(finalizeWidths);
    return true;
  }

  const tryRevealSoon = debounce(tryReveal, 16);

  function start() {
    const mount = getMount();
    if (!mount) return;

    if (tryReveal()) return;

    const mo = new MutationObserver(tryRevealSoon);
    mo.observe(mount, { childList: true, subtree: true, characterData: true });

    document.addEventListener('shopify:section:load', function () {
      setState('hydrating');
      tryRevealSoon();
    });

    window.setTimeout(function () {
      if (isReady()) return;
      process();
      setState('ready');
      requestAnimationFrame(finalizeWidths);
    }, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
